import {
  dateCompare,
  dateRangeToDateStringArray,
  dateStringRangeToDateStringArray,
  getDayOfWeekDaysForNext3Years,
  getMonthDaysForNext3Years,
  toPrecision,
} from '@curbnturf/helpers';
import { DateTime } from 'luxon';
import { ICancellationPolicyOption, ICheckInProcessType, IReservationProcessType, RatingType } from '../enums';
import {
  IAccessRoute,
  IAddress,
  ICoordinateGis,
  IListing,
  IListingExtra,
  IListingRating,
  IListingTax,
  IMessage,
  IMessageChannel,
  IPolicy,
  ISignalReception,
  ISubPhoto,
  ITax,
} from '../interfaces';
import { AccessRoute } from './access-route';
import { Activity } from './activity';
import { Amenity } from './amenity';
import { CoordinateGis } from './coordinates-gis';
import { LandOwner } from './land-owner';
import { ListingFavorite } from './listing-favorite';
import { ListingRating } from './listing-rating';
import { RoadConditionDescribed } from './road-condition-described';
import { Site } from './site';
import { Tax } from './tax';
import { User } from './user';

export class Listing implements IListing {
  id?: number;
  syncId?: string;
  user?: User;
  available?: boolean;
  featured?: boolean;
  name?: string;
  landTypes?: IListing['landTypes'];
  primaryType?: IListing['primaryType'];
  category?: IListing['category'];
  descriptors?: IListing['descriptors'];
  weekdayDiscount?: number;
  nextReservationDiscount?: number;
  reservationProcess?: IReservationProcessType['type'];
  checkInProcess?: ICheckInProcessType['type'];
  checkIn?: string; // time string '04:00PM'
  checkOut?: string; // time string '11:00AM'
  policies?: IPolicy[];
  maxRVAge?: number;
  minLengthOfStayInDays?: number;
  maxLengthOfStayInDays?: number;
  reserveDaysInAdvance?: number;
  bookingWindow?: number;
  cancellationPolicy?: ICancellationPolicyOption['option'];
  blackoutDays?: string[]; // array of date strings '2018-12-25'
  blackoutMonths?: number[]; // array of months for which the property is unavailable (1 = January, 12 = December)
  blackoutDayOfWeek?: string[]; // array of the days of the week that are unavailable
  photos?: ISubPhoto[]; // array URL strings
  description?: string;
  size?: number; // square feet
  extras?: IListingExtra[];
  taxes?: ITax[];
  listingTaxes?: IListingTax[]; // used since TypeORM Many-to-Many in Postgres seems to be broken
  ratings?: IListingRating[];
  rating?: number;
  ratingCount?: number;
  messageChannels?: IMessageChannel[];
  signalReception?: ISignalReception[];
  messages?: IMessage[];
  deleted?: number;
  coordinates?: ICoordinateGis;
  landOwner?: LandOwner;
  address?: IAddress;
  youtubeCode?: string;
  rvcastUrl?: string;
  created?: number;
  publishDate?: number;
  potentialHazards?: boolean;
  potentialHazardDescription?: string;

  timezone?: string;

  favoritedUsers?: ListingFavorite[];

  sites?: Site[];

  modified?: number;

  get nameId(): string {
    return `${this.name} (${this.id})`;
  }

  get roadCondition(): RoadConditionDescribed {
    if (this.sites) {
      return Listing.mergeRoadCondition(this.sites.flatMap((site) => site.accessRoutes ?? []));
    }

    return new RoadConditionDescribed();
  }

  static mergeRoadCondition(accessRoutes: IAccessRoute[]): RoadConditionDescribed {
    return accessRoutes.reduce(
      (conditionArray: RoadConditionDescribed, route: IAccessRoute) => {
        if (route && route.roadCondition) {
          const roadCondition = route.roadCondition;
          for (const key in conditionArray) {
            if (Object.prototype.hasOwnProperty.call(conditionArray, key)) {
              if (roadCondition[key]) {
                conditionArray[key] = roadCondition[key];
              }
            }
          }
        }
        return conditionArray;
      },
      {
        paved: false,
        pavedDescription: '',
        smoothGravel: false,
        smoothGravelDescription: '',
        poorGravel: false,
        poorGravelDescription: '',
        highClearance: false,
        highClearanceDescription: '',
        offRoad: false,
        offRoadDescription: '',
      } as RoadConditionDescribed,
    );
  }

  static getName(listing: IListing): string {
    if (listing.id === 1) {
      return 'Curb<span class="trinidad">N</span>Turf HQ';
    }

    return listing.name || 'Your New Property';
  }

  static getNameNoHTML(listing: IListing): string {
    if (listing.id === 1) {
      return 'CurbNTurf HQ';
    }

    return listing.name || 'Your New Property';
  }

  constructor(data?: IListing) {
    if (data) {
      this.setData(data);
    }
  }

  setData(data: IListing): void {
    for (const key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (this as any)[key] = (data as any)[key];
      }
    }

    if (!this.blackoutDays) {
      this.blackoutDays = [];
    }

    if (!this.blackoutMonths) {
      this.blackoutMonths = [];
    } else {
      this.blackoutMonths = this.blackoutMonths.map((blackoutMonth) => {
        if (typeof blackoutMonth === 'string') {
          return parseInt(blackoutMonth, 10);
        }

        return blackoutMonth;
      });
    }

    if (!this.photos) {
      this.photos = [];
    }

    if (!this.extras) {
      this.extras = [];
    }

    if (!this.signalReception) {
      this.signalReception = [];
    }

    // todo: save these numbers so they only have to be calculated when a rating is created
    if (!this.ratings) {
      this.ratings = [];
    } else if (!this.ratingCount && !this.rating) {
      const overallRating = this.ratings.filter((rating) => rating.topic === RatingType.overall);

      this.ratingCount = overallRating.length;
      if (!this.ratingCount) {
        this.rating = 0;
      } else {
        this.rating = toPrecision(
          overallRating.reduce(
            (total, rating: IListingRating) =>
              (total = total + (rating && rating.score && rating.score.score ? rating.score.score : 0)),
            0,
          ) / this.ratingCount,
          1,
        );
      }
    }

    if (!this.policies) {
      this.policies = [];
    }

    this.setTaxData();

    this.coordinates = new CoordinateGis(this.coordinates);

    if (!this.landTypes) {
      this.landTypes = [];
    }

    this.landOwner = new LandOwner(this.landOwner);

    if (!this.sites) {
      this.sites = [];
    }

    this.user = new User(this.user);
  }

  getBusyDays(): string[] {
    if (!this.available) {
      return [];
    }

    return this.getBlackoutDays()
      .filter((days) => !!days)
      .sort(dateCompare);
  }

  getBlackoutDays(): string[] {
    const blackoutDays: string[] = [];

    if (this.blackoutDays) {
      blackoutDays.push(
        ...this.blackoutDays.reduce((days: string[], blackout) => {
          days.push(...dateStringRangeToDateStringArray(blackout));

          return days;
        }, []),
      );
    }

    if (this.blackoutMonths) {
      this.blackoutMonths.forEach((month) => blackoutDays.push(...getMonthDaysForNext3Years(month)));
    }

    if (this.blackoutDayOfWeek) {
      this.blackoutDayOfWeek.forEach((day) => blackoutDays.push(...getDayOfWeekDaysForNext3Years(day)));
    }

    return blackoutDays;
  }

  /** Listing-level counterpart to `Site.isAvailableForRange` — checks only the
   *  listing's own blackout settings (which every site under it inherits via
   *  `Site.getBlackoutDays()`), not availability/reservations, since those are
   *  per-site. Used as a cheap first pass before checking individual sites. */
  isAvailableForRange(startIso: string, endIso: string): boolean {
    if (!startIso || !endIso) {
      return false;
    }

    const start = DateTime.fromISO(startIso);
    const lastNight = DateTime.fromISO(endIso).minus({ days: 1 });

    if (!start.isValid || !lastNight.isValid || start > lastNight) {
      return true;
    }

    const blackoutDays = new Set(this.getBlackoutDays());
    return !dateRangeToDateStringArray(start, lastNight).some((night) => blackoutDays.has(night));
  }

  getName(): string {
    return Listing.getName(this);
  }

  getNameNoHTML(): string {
    return Listing.getNameNoHTML(this);
  }

  getRating(): number {
    if (this.rating || this.rating === 0) {
      return this.rating;
    }

    return ListingRating.calculateRating(this.ratings);
  }

  getRatingCount(): number {
    if (this.ratingCount || this.ratingCount === 0) {
      return this.ratingCount;
    }

    return ListingRating.calculateRatingCount(this.ratings) || 0;
  }

  getFinishedPercent(): number {
    return 1;
  }

  displayLocation(): string {
    if (!this.address || (!this.address.city && !this.address.state)) {
      return '';
    }

    if (!this.address.city) {
      return `${this.address.state}`;
    }

    return `${this.capitalize(this.address.city.toLocaleLowerCase())}, ${this.address.state}`;
  }

  getAccumulatedAmenities(): Amenity[] {
    const amenities: Amenity[] = [];
    this.sites?.forEach((site) => {
      site.amenities?.forEach((amenity) => {
        amenities.push(amenity);
      });
    });

    return amenities;
  }

  getAccumulatedActivities(): Activity[] {
    const activities: Activity[] = [];
    this.sites?.forEach((site) => {
      site.activities?.forEach((activity) => {
        activities.push(activity);
      });
    });

    return activities;
  }

  getAccumulatedAccessRoutes(): AccessRoute[] {
    const accessRoutes: AccessRoute[] = [];
    this.sites?.forEach((site) => {
      site.accessRoutes?.forEach((route) => {
        accessRoutes.push(route);
      });
    });

    return accessRoutes;
  }

  private setTaxData(): void {
    if (this.taxes && this.taxes.length > 0) {
      this.taxes = this.taxes.map((tax) => new Tax(tax));
      this.listingTaxes = [];
    } else if (this.listingTaxes && this.listingTaxes.length > 0) {
      this.taxes = this.listingTaxes.map((propertyTax) => new Tax(propertyTax.tax));
    } else {
      this.taxes = [];
      this.listingTaxes = [];
    }
  }

  private capitalize(str: string): string {
    return str.replace(/\w\S*/g, (word) => word.replace(/^\w/, (character) => character.toUpperCase()));
  }

  getMinPriceWithTax() {
    if (this.sites && Array.isArray(this.sites) && this.sites.length > 0) {
      const prices = this.sites.filter((site: Site) => site.price && site.price > 0);
      if (prices.length === 0) {
        prices.push(new Site({ price: 0 }));
      }
      return Math.min(...prices.map((site: Site) => new Site({ ...site }).getPriceWithTax(this)));
    } else {
      // Was 0 now it's 10, maybe it should be more calculated somehow, not sure.
      return 10;
    }
  }

  getMaxPriceWithTax() {
    if (this.sites && Array.isArray(this.sites) && this.sites.length > 0) {
      const prices = this.sites.filter((site: Site) => site.price && site.price > 0);
      if (prices.length === 0) {
        prices.push(new Site({ price: 0 }));
      }
      return Math.max(...prices.map((site: Site) => new Site({ ...site }).getPriceWithTax(this)));
    } else {
      // Was 0 now it's 10, maybe it should be more calculated somehow, not sure.
      return 10;
    }
  }

  getMinPrice() {
    if (this.sites && Array.isArray(this.sites) && this.sites.length > 0) {
      const prices = this.sites.filter((site: Site) => site.price && site.price > 0);
      if (prices.length === 0) {
        prices.push(new Site({ price: 0 }));
      }
      return Math.min(...prices.map((site: Site) => new Site({ ...site }).getPrice()));
    } else {
      // Was 0 now it's 10, maybe it should be more calculated somehow, not sure.
      return 10;
    }
  }

  getMaxPrice() {
    if (this.sites && Array.isArray(this.sites) && this.sites.length > 0) {
      const prices = this.sites.filter((site: Site) => site.price && site.price > 0);
      if (prices.length === 0) {
        prices.push(new Site({ price: 0 }));
      }
      return Math.max(...prices.map((site: Site) => new Site({ ...site }).getPrice()));
    } else {
      // Was 0 now it's 10, maybe it should be more calculated somehow, not sure.
      return 10;
    }
  }
}
